import React, { useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, ScrollView, Platform, Image, Alert , Linking } from 'react-native';
import { whatsappSoporte } from '../constants/contacto';
import { useMargenesSeguros } from '../components/PantallaSegura';
import { useRouter } from 'expo-router';
import { Button } from '../components/Button';
import { Colors, SIZES } from '../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore } from '../store/useAppStore';
import ScreenBackground from '../components/ScreenBackground';
import { entrarALaApp } from '../utils/volver';
import { authBridge, prepararCambioDeModo } from '../services/authBridge';
import { callSetDriverRole } from '../services/cloudFunctions';
import { VERTICAL_ESCOLAR_VISIBLE, KYC_WALL_ENFORCED } from '../constants/featureFlags';
import { aplazarVerificacionEnEstaSesion, rutaSegunRol } from './_layout';
import { mensajeDeError } from '../utils/mensajeDeError';

export default function KycWelcomeScreen() {
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  const isVerified = useAppStore((state) => state.isVerified);
  const isDriverVerified = useAppStore((state) => state.isDriverVerified);
  const userFirebaseUid = useAppStore((state) => state.userFirebaseUid);
  const setRole = useAppStore((state) => state.setRole);
  const setBaseRole = useAppStore((state) => state.setBaseRole);
  const [selectedVehicle, setSelectedVehicle] = useState<'CAR' | 'MOTORCYCLE' | 'MINIBUS' | 'SCHOOL_BUS' | 'DELIVERY' | null>(null);

  // ¿A quién se le pregunta por un vehículo? SOLO a quien va a conducir.
  //
  // Antes la condición era al revés: "todo el que no sea PASSENGER ni
  // FOOD_DELIVERY". Con eso, el dueño de rent car y el que busca vehículo
  // —que role-selection manda aquí— aterrizaban en "¿Qué tipo de vehículo
  // conduces?" con "Continuar" apagado hasta elegir uno, y si elegían, el
  // guardado los convertía en DRIVER. Y quien llega sin rol (desde el perfil,
  // o por el recordatorio) caía en la misma pantalla muerta.
  const esChofer = role === 'DRIVER' || role === 'SCHOOL_DRIVER' || role === 'DELIVERY_DRIVER';
  // ══════════════════════════════════════════════════════════════════════
  // "AHORA NO" — LA SALIDA DEL EMBUDO.
  //
  // Esta pantalla es el destino de SIETE sitios distintos (el guardián del
  // _layout, auth.tsx, verify-email.tsx, index.tsx, role-selection.tsx,
  // profile.tsx y map.tsx). Hasta ahora no tenía forma de seguir a la app sin
  // fotografiar una cédula dominicana: quien se registraba y no tenía la
  // cédula a mano se quedaba aquí para siempre. Un solo botón aquí abre la
  // salida para los siete caminos a la vez.
  //
  // Con KYC_WALL_ENFORCED = false (estado actual) la verificación NO es
  // obligatoria para entrar: se aplaza durante esta sesión y el usuario va a
  // la pantalla de su rol. Puede volver cuando quiera desde su perfil.
  // ══════════════════════════════════════════════════════════════════════
  // ══════════════════════════════════════════════════════════════════════
  // EL QUE YA ESTA DENTRO NO ESTA DANDOSE DE ALTA
  // ══════════════════════════════════════════════════════════════════════
  // EL FALLO, REPORTADO EN CAMPO (1-oct-2026): «la pantalla KYC lo retiene, y
  // al intentar salir varias veces rompe el menu llevando al usuario a la
  // pantalla fea como pidiendo otra vez que se registre; y la del lado derecho
  // a veces aparece para cerrar sesion en vez de volver a su perfil».
  //
  // Tenia razon, y la cabecera lo explica entera. Para un chofer YA VERIFICADO
  // que entra aqui desde su perfil habia exactamente DOS salidas:
  //   · izquierda, con una flecha de «atras» que invita a volver: en realidad
  //     es `handleChangeRole`, que le BORRA EL ROL (`setRole(null)`) y lo
  //     manda a /role-selection — la «pantalla fea» que le pide elegir otra vez
  //     como quiere usar la app, como si se estuviera registrando;
  //   · derecha: cerrar sesion.
  // Ninguna de las dos lo devuelve a su perfil. No es que se rompiera el menu:
  // es que no habia por donde salir.
  //
  // Las dos siguen existiendo —son la red de seguridad real de quien se
  // equivoco de rol al registrarse, y de quien se queda encerrado sin rol—,
  // pero solo para QUIEN ESTA EN EL ALTA. Al que ya esta verificado se le da lo
  // que pedia: volver.
  const yaEstaDentro = !!role && (isVerified || isDriverVerified);

  /** Volver por donde vino. Sin historial, a la pantalla de su rol. */
  const volverASuPerfil = () => {
    if (router.canGoBack()) router.back();
    else router.replace(rutaSegunRol(role) as any);
  };

  const handleAhoraNo = () => {
    aplazarVerificacionEnEstaSesion();
    // Mismo motivo que en kyc-documents: un replace hacia las pestañas desde el
    // stack raíz apila un segundo menú encima del que ya hay.
    entrarALaApp(router, rutaSegunRol(role));
  };

  // Salida explícita del embudo de KYC: limpia el rol en el store y manda
  // al picker principal.
  // Caso real: user tocó "Conductor" + "MOTORCYCLE" por error y quiere ser
  // pasajero. Antes la única salida era cerrar la app, y al reabrir lo
  // mandaba de nuevo aquí.
  // Aplazamos también aquí: /role-selection es ruta de entrada, así que sin
  // esto el guardián del _layout lo devolvería a esta misma pantalla antes de
  // que llegara a ver el selector.
  const cambiandoRolRef = React.useRef(false);
  const handleChangeRole = async () => {
    if (cambiandoRolRef.current) return;
    cambiandoRolRef.current = true;
    try {
      // H09 / H10, auditoría 16-sep-2026 — mismo control que las tarjetas de
      // "Modo de usuario" del perfil: un chofer en línea (o con un viaje vivo)
      // que soltara el rol aquí quedaba como chofer fantasma, con el GPS
      // publicando y sin botón para apagarlo. Sin viaje ni GPS vivo —el caso de
      // casi todo el que llega a esta pantalla— esto responde al instante.
      const resultado = await prepararCambioDeModo(userFirebaseUid, null);
      if (!resultado.ok) {
        // En web `Alert.alert` no pinta nada y el botón parecería muerto (V35).
        if (Platform.OS === 'web') window.alert(resultado.titulo + '\n\n' + resultado.mensaje);
        else Alert.alert(resultado.titulo, resultado.mensaje);
        return;
      }
    } catch (e: any) {
      // Si la comprobación misma falla no se deja al usuario encerrado en el
      // embudo: esta es su salida. Se sigue como hasta hoy.
      console.warn('[kyc-welcome] preparar el cambio de rol falló:', e?.message ?? e);
    } finally {
      cambiandoRolRef.current = false;
    }
    aplazarVerificacionEnEstaSesion();
    setRole(null);
    setBaseRole(null);
    // ⚠️ `dismissTo` Y NO `replace`, Y ESTE ES EL ARREGLO DE "HAY QUE TOCAR LA
    //    FLECHA DOS VECES".
    //
    // A esta pantalla se llega con `push` desde /role-selection
    // (role-selection.tsx), así que la pila es […, /role-selection, /kyc-welcome].
    // Con `replace`, /kyc-welcome se sustituye por OTRO /role-selection y la pila
    // queda […, /role-selection, /role-selection]: dos pantallas idénticas pegadas.
    //
    // Entonces el primer toque de "Volver" hace `back()` y aterriza en una
    // pantalla exactamente igual — desde fuera parece que la flecha no hizo
    // nada, y por eso el fundador la toca DOS VECES. El segundo toque sí baja,
    // el guardián del _layout vuelve a enrutar y esta pantalla se monta de cero
    // con el desplazamiento en 0: la segunda tarjeta aparece detrás del pie.
    //
    // `dismissTo` vuelve a la instancia de /role-selection QUE YA EXISTE en vez
    // de apilar otra. Una sola pantalla, una sola flecha, un solo toque.
    const ir: any = router as any;
    if (typeof ir.dismissTo === 'function') ir.dismissTo('/role-selection');
    else router.replace('/role-selection');
  };

  // Última línea de defensa: cerrar sesión completa. Resuelve el loop del
  // layout guard que restaura el rol desde Firestore aunque el store lo
  // limpie — al cerrar la sesión el guard bails early con uid=null.
  const handleSignOut = () => {
    Alert.alert(
      'Cerrar Sesión',
      '¿Seguro que quieres salir? Perderás el progreso de KYC no guardado.',
      [
        { text: 'Quedarse', style: 'cancel' },
        {
          text: 'Cerrar Sesión',
          style: 'destructive',
          onPress: async () => {
            setRole(null);
            setBaseRole(null);
            try {
              await authBridge.signOut();
            } catch (e) {
              console.warn('[kyc-welcome] signOut error:', e);
            }
            router.replace('/auth');
          },
        },
      ]
    );
  };

  const handleNext = async () => {
    // QA brief — Fix DELAY al tocar Continuar:
    //
    // Antes esta función era `async` y hacía `await firestoreService.X(...)`
    // ANTES de `router.push(...)`. El SDK web de Firestore puede colgarse
    // 6-8 segundos en RN+Hermes en algunos devices (mismo problema que ya
    // documentamos en /_layout.tsx con timeout protector). Resultado: tocar
    // "Continuar" causaba un delay perceptible antes de ver la siguiente
    // pantalla.
    //
    // Fix: la persistencia Firestore ahora es FIRE-AND-FORGET. La función
    // dejó de ser async — `router.push` corre inmediatamente. El doc
    // /users/{uid} se actualiza en background; las Cloud Functions que
    // leen `role` y `vehicleType` para getMonthlyFee corren cuando el user
    // efectivamente se pone online (mucho después del KYC), así que la
    // carrera está cubierta.
    //
    // Cero regresión: mismas escrituras, mismos rol/vehicleType, mismo
    // destino (kyc-documents). Lo único que cambia es que el UI no bloquea.

    if (!esChofer) {
      router.push({
        pathname: '/kyc-documents',
        params: { vehicle: role ?? '' }
      });
      return;
    }
    if (!selectedVehicle) return;

    // FIX CRÍTICO LAUNCH — Antes este bloque hacía writes directos a
    // Firestore con `role` desde el cliente, pero las rules bloquean ese
    // campo silenciosamente. Resultado: para SCHOOL_DRIVER y DELIVERY_DRIVER
    // el rol nunca llegaba a Firestore → al reiniciar la app el guard
    // leía role=undefined → rebotaba al picker. Pasajero funcionaba bien
    // porque NO necesitaba updateProfile (role queda como en signup).
    //
    // Ahora usamos el endpoint backend /api/users/set-driver-role que
    // pasa por Admin SDK (bypasses rules con validación server-side).
    let nextRole: 'DRIVER' | 'SCHOOL_DRIVER' | 'DELIVERY_DRIVER' = 'DRIVER';
    let nextVehicleType: 'CAR' | 'MOTORCYCLE' | 'MINIBUS' | 'SCHOOL_BUS' | 'BICYCLE' | undefined = undefined;
    if (selectedVehicle === 'SCHOOL_BUS' && !VERTICAL_ESCOLAR_VISIBLE) {
      // Red de seguridad: la tarjeta escolar ya está oculta, pero si el valor
      // llegara aquí por un estado viejo o un enlace, NO se graba un rol que
      // hoy no tiene pantallas. Se degrada a chofer con minibús.
      nextRole = 'DRIVER';
      nextVehicleType = 'MINIBUS';
    } else if (selectedVehicle === 'SCHOOL_BUS') {
      nextRole = 'SCHOOL_DRIVER';
      nextVehicleType = 'SCHOOL_BUS';
    } else if (selectedVehicle === 'DELIVERY') {
      nextRole = 'DELIVERY_DRIVER';
      nextVehicleType = 'MOTORCYCLE';
    } else {
      nextRole = 'DRIVER';
      nextVehicleType = selectedVehicle as 'CAR' | 'MOTORCYCLE' | 'MINIBUS';
    }
    // ══════════════════════════════════════════════════════════════════════
    // EL ROL SE APLICA CUANDO EL SERVIDOR DICE QUE SÍ. NO ANTES.
    // ══════════════════════════════════════════════════════════════════════
    // Los `setRole(...)` vivían aquí arriba, en cada rama, ANTES de llamar a
    // `setDriverRole`. Y esa llamada RECHAZA —con `failed-precondition`— a
    // quien ya está registrado con otro rol: un dueño de rent car, un
    // restaurante, un cliente de comida. El código avisaba y hacía `return`…
    // pero el rol local YA estaba cambiado y nadie lo devolvía a su sitio.
    //
    // Resultado medido: un dueño de rent car que cae en esta pantalla y toca
    // "Carro" se queda con la app creyendo que es chofer mientras el servidor
    // sigue diciendo que es dueño de rent car. Desde ahí el CRM deja de
    // funcionarle —las reglas de /vehicles miran el rol DEL SERVIDOR— y las
    // pantallas que ve son las que no le tocan. Es el «me muestra los motores
    // como si fuera a registrarme por primera vez».
    //
    // El `setRole` que faltaba en la rama de carro/motor/minibús (y que arregló
    // en su día lo del minibús que caía en el panel escolar) sigue estando:
    // ahora lo hace `aplicarRolLocal`, para las cuatro ramas por igual, una vez
    // que el servidor lo ha aceptado.
    const aplicarRolLocal = () => {
      setRole(nextRole === 'SCHOOL_DRIVER' ? 'SCHOOL_DRIVER' : nextRole === 'DELIVERY_DRIVER' ? 'DELIVERY_DRIVER' : 'DRIVER');
      setBaseRole(nextRole);
    };
    // Fire-and-forget: el endpoint Railway escribe role + vehicleType +
    // baseRole vía Admin SDK. Si falla, queda log warning — pero el flujo
    // de UI sigue. Las Cloud Functions que leen role/vehicleType solo
    // corren al ponerse online, mucho después.
    // GUARDAR EL ROL — ahora por Cloud Function, no por Railway.
    //
    // Antes esto llamaba al backend de Railway, que está CAÍDO: la petición
    // fallaba, solo se dejaba un aviso en el log y la pantalla continuaba como
    // si todo hubiera salido bien. El rol jamás llegaba a la base de datos, así
    // que al reabrir la app el usuario volvía a su rol viejo. Por eso el
    // fundador eligió minibús y terminó en el panel de transporte escolar.
    //
    // Y ya NO es fire-and-forget: si no se puede guardar, se le dice y no se
    // le deja seguir creyendo que quedó registrado.
    if (userFirebaseUid) {
      try {
        await callSetDriverRole({ role: nextRole, vehicleType: nextVehicleType ?? null });
        aplicarRolLocal();
      } catch (e: any) {
        console.warn('[kyc-welcome] setDriverRole falló:', e?.code, e?.message);
        // ── DECIR EL MOTIVO DE VERDAD ──────────────────────────────────────
        // Antes SIEMPRE se culpaba a la conexión. Pero el servidor rechaza a
        // quien ya está registrado con otro rol (un restaurante, un dueño de
        // rent car, un cliente de comida) con un mensaje claro y su propio
        // código. Esa persona reintentaba una y otra vez creyendo que era su
        // internet, sin llegar nunca a ninguna parte y sin saber a quién
        // escribir.
        const esReglaDeNegocio = String(e?.code ?? '').includes('failed-precondition');
        Alert.alert(
          esReglaDeNegocio ? 'No podemos cambiar tu rol desde aquí' : 'No pudimos guardar tu selección',
          esReglaDeNegocio
            ? `${mensajeDeError(e, 'Tu cuenta está registrada con otro rol.')}

Escríbenos por WhatsApp y lo cambiamos nosotros.`
            : 'Revisa tu conexión e inténtalo de nuevo. Si sigues, tu perfil podría quedar con el vehículo equivocado.',
          esReglaDeNegocio
            ? [
                { text: 'Cerrar', style: 'cancel' as const },
                { text: 'Escribir por WhatsApp', onPress: () => Linking.openURL(whatsappSoporte('Hola, necesito cambiar el rol de mi cuenta en Go Safely.')).catch(() => {}) },
              ]
            : [{ text: 'Entendido' }]
        );
        return;
      }
    } else {
      // Sin sesión no hay a quién preguntarle: se aplica igual, como siempre.
      // El guardián del _layout lo volverá a enrutar cuando la sesión llegue.
      aplicarRolLocal();
    }

    router.push({
      pathname: '/kyc-documents',
      params: { vehicle: selectedVehicle }
    });
  };

  /**
   * ALTO REAL DEL PIE, MEDIDO — no adivinado.
   *
   * El pie flota en `position: absolute`, así que la lista tiene que reservar
   * ese hueco abajo o la última tarjeta queda debajo y no se puede tocar. Antes
   * ese hueco era `paddingBottom: 230`, un número escrito a mano.
   *
   * Y el pie NO mide siempre lo mismo: lleva uno o dos botones según
   * `KYC_WALL_ENFORCED`, y su nota cambia de texto con el rol — la del chofer es
   * la más larga de las tres— así que ocupa más líneas. Encima el número de
   * líneas depende del ancho del teléfono y del tamaño de letra que el usuario
   * tenga puesto en Android. Con el pie por encima de 230, la tarjeta
   * "Motorista (Taxi)" queda cortada — que es lo que reportó el fundador con
   * una captura.
   *
   * Mismo patrón que `altoHoja` en el mapa: se mide con `onLayout` y se reserva
   * lo que de verdad ocupa. El 230 se queda solo como valor de arranque, para
   * el primer fotograma antes de que llegue la medida.
   */
  const [altoDelPie, setAltoDelPie] = useState(230);

  /**
   * EL PIE SE METIA DEBAJO DE LA BARRA DE NAVEGACION DEL TELEFONO.
   *
   * Esta pantalla usa el `SafeAreaView` de 'react-native', que en Android es un
   * `<View>` vacio — no reserva NADA (lo documenta components/PantallaSegura.tsx).
   * Y el proyecto compila con `edgeToEdgeEnabled=true` (android/gradle.properties),
   * o sea que la app dibuja por debajo de la barra de navegacion del sistema.
   *
   * Resultado: el pie, que esta en `position: absolute; bottom: 0`, quedaba
   * PARCIALMENTE TAPADO por la barra del sistema, y la nota de abajo
   * ("Puedes entrar y conocer la app ahora mismo...") se perdia ahi. En la
   * captura del fundador se ve el texto pegado al borde inferior.
   */
  const { abajo: margenAbajo } = useMargenesSeguros();

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        {/* Sticky header con salida explícita. Antes solo había un chevron
            chico dentro del ScrollView que se ocultaba al desplazar — y
            volver(router) podía caer al index donde el guard del _layout
            rebotaba al user aquí mismo. Con esto, una sola pulsación
            limpia el rol y abre el picker principal sin pelearse con el
            guard. */}
        <View style={[styles.stickyHeader, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
          {/* La flecha hace lo que la flecha promete. Ver `yaEstaDentro`. */}
          <TouchableOpacity
            onPress={yaEstaDentro ? volverASuPerfil : handleChangeRole}
            style={styles.stickyBackBtn}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={yaEstaDentro ? 'Volver a mi perfil' : 'Cambiar de rol'}
          >
            {/* EL ICONO TAMBIEN TIENE QUE DECIR LA VERDAD.
                Cuando `yaEstaDentro` es false este boton NO vuelve: cambia de
                rol —borra el rol guardado y abre el selector—. Con una flecha
                de «atras» encima, el fundador lo tomo por una salida y lo que
                vio fue «la ventana fea de elegir perfil». El texto ya decia
                «Cambiar de rol»; el icono seguia diciendo lo contrario. */}
            <Ionicons
              name={yaEstaDentro ? 'arrow-back' : 'swap-horizontal'}
              size={20}
              color={Colors.primary}
              style={{ marginRight: 6 }}
            />
            <Text style={styles.stickyBackText}>{yaEstaDentro ? 'Volver' : 'Cambiar de rol'}</Text>
          </TouchableOpacity>
          {/* Cerrar sesion es la red de seguridad de quien se queda encerrado
              SIN rol en el alta. A quien ya tiene su perfil hecho no se le pone
              al lado de la salida: es lo que el fundador vio como «aparece
              cerrar sesion en vez de volver a mi perfil». */}
          {!yaEstaDentro && (
            <TouchableOpacity onPress={handleSignOut} style={styles.stickySignOutBtn} activeOpacity={0.7}>
              <Ionicons name="log-out-outline" size={18} color="#FF3B30" style={{ marginRight: 4 }} />
              <Text style={styles.stickySignOutText}>Salir</Text>
            </TouchableOpacity>
          )}
        </View>
        <ScrollView
          contentContainerStyle={[
            styles.container,
            // +24 de respiro: pegar la última tarjeta al borde del pie se ve
            // como si estuviera cortada aunque quepa entera.
            { paddingBottom: altoDelPie + 24 },
          ]}
        >
        <View style={styles.header}>
          <Text style={styles.title}>¡Bienvenido a Go Safely!</Text>
          {/* Decía "viajes y entregas 100% seguros y confiables". Go Safely es
              un puente tecnológico entre particulares (terms.tsx punto 2) y su
              punto 10 limita expresamente su responsabilidad: no puede
              garantizar el 100% de nada que ocurra dentro del carro de otro.
              Se dice lo que la verificación SÍ logra. */}
          <Text style={styles.subtitle}>
            {!esChofer
              ? 'Para tu seguridad y la de los choferes, validamos la identidad de los miembros de la plataforma. Así hay menos cuentas falsas y sabes con quién viajas.'
              : 'Para garantizar la seguridad de todos y activar tu cuenta con 0% de comisión, necesitamos validar tu identidad.'}
          </Text>
        </View>

        {!esChofer ? (
          <View style={{ marginBottom: 20 }}>
            <View style={[styles.card, { backgroundColor: '#F0FFF5', borderColor: Colors.accent, borderStyle: 'solid' }]}>
              {/* El título era "Beneficios del Perfil Biométrico". Aquí NO hay
                  perfil biométrico: privacy.tsx punto 2.c dice que la selfie y
                  la cédula "las compara a mano una persona autorizada" y que
                  "no generamos ni almacenamos plantillas ni vectores
                  faciales"; terms.tsx punto 4 lo repite. La palabra
                  "biométrico" en la app es lo que ya nos costó un rechazo por
                  la declaración de datos biométricos. */}
              <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 12}}>
                <Text style={{fontSize: 24, marginRight: 8}}>🛡️</Text>
                <Text style={{fontSize: 16, fontWeight: 'bold', color: Colors.primary}}>Beneficios de verificar tu identidad</Text>
              </View>
              <Text style={{color: Colors.text, fontSize: 14, lineHeight: 20, marginBottom: 12}}>
                Una vez verifiques tu identidad, recibirás la insignia de <Text style={{fontWeight: 'bold', color: Colors.accent}}>Escudo Verde</Text> junto a tu foto de perfil.
              </Text>
              <Text style={{color: Colors.textSecondary, fontSize: 13, lineHeight: 18}}>
                • Los choferes verán tu escudo y sabrán que eres un pasajero real.{"\n"}
                • Fomentamos una comunidad transparente y libre de cuentas falsas o spam.{"\n"}
                • Tus datos personales viajan por conexión segura (TLS) y se guardan cifrados en reposo.
              </Text>
            </View>
          </View>
        ) : (
          <>
            <Text style={styles.sectionTitle}>¿Qué tipo de vehículo conduces?</Text>
            
            <View style={styles.optionsContainer}>
              <TouchableOpacity 
                style={[styles.card, selectedVehicle === 'CAR' && styles.cardSelected]}
                onPress={() => setSelectedVehicle('CAR')}
                activeOpacity={0.8}
              >
                <View style={[styles.iconCircle, selectedVehicle === 'CAR' && styles.iconCircleSelected]}>
                  <Image source={require('../../assets/images/carro.png')} style={styles.vehicleIcon} resizeMode="contain" />
                </View>
                <Text style={[styles.cardTitle, selectedVehicle === 'CAR' && styles.textSelected]}>Carro</Text>
                <Text style={styles.cardDesc}>Hasta 4 pasajeros</Text>
                <Text style={styles.feeBadge}>RD$ 1,300/mes (30 días de prueba gratis)</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.card, selectedVehicle === 'MOTORCYCLE' && styles.cardSelected]}
                onPress={() => setSelectedVehicle('MOTORCYCLE')}
                activeOpacity={0.8}
              >
                <View style={[styles.iconCircle, selectedVehicle === 'MOTORCYCLE' && styles.iconCircleSelected]}>
                  <Image source={require('../../assets/images/motor.png')} style={styles.vehicleIcon} resizeMode="contain" />
                </View>
                <Text style={[styles.cardTitle, selectedVehicle === 'MOTORCYCLE' && styles.textSelected]}>Motorista (Taxi)</Text>
                <Text style={styles.cardDesc}>Transporta pasajeros. Tu casco debe estar rotulado con tu placa. El 2do casco (pasajero) es opcional.</Text>
                <Text style={styles.feeBadge}>RD$ 1,000/mes (30 días de prueba gratis)</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.card, selectedVehicle === 'DELIVERY' && styles.cardSelected]}
                onPress={() => setSelectedVehicle('DELIVERY')}
                activeOpacity={0.8}
              >
                <View style={[styles.iconCircle, selectedVehicle === 'DELIVERY' && styles.iconCircleSelected]}>
                  <Image source={require('../../assets/images/motor.png')} style={styles.vehicleIcon} resizeMode="contain" />
                </View>
                <Text style={[styles.cardTitle, selectedVehicle === 'DELIVERY' && styles.textSelected]}>Repartidor (Delivery)</Text>
                <Text style={styles.cardDesc}>Entrega comida y paquetes a domicilio en moto o bicicleta.</Text>
                <Text style={styles.feeBadge}>RD$ 1,000/mes (30 días de prueba gratis)</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.card, selectedVehicle === 'MINIBUS' && styles.cardSelected]}
                onPress={() => setSelectedVehicle('MINIBUS')}
                activeOpacity={0.8}
              >
                <View style={[styles.iconCircle, selectedVehicle === 'MINIBUS' && styles.iconCircleSelected]}>
                  <Image source={require('../../assets/images/mini bus.png')} style={styles.vehicleIcon} resizeMode="contain" />
                </View>
                <Text style={[styles.cardTitle, selectedVehicle === 'MINIBUS' && styles.textSelected]}>Minibus</Text>
                <Text style={styles.cardDesc}>Más de 6 pasajeros</Text>
                <Text style={styles.feeBadge}>RD$ 1,500/mes (30 días de prueba gratis)</Text>
              </TouchableOpacity>

              {/* Esta es la PRIMERA pantalla de todo chofer nuevo. Con el
                  vertical escolar oculto, elegir aquí "Transporte Escolar"
                  grababa el rol SCHOOL_DRIVER en Firestore y aterrizaba en un
                  mapa rotulado "Modo Transporte Escolar" sin un solo control
                  — los controles de chofer solo existen para DRIVER y
                  DELIVERY_DRIVER. Se oculta con la misma bandera. */}
              {VERTICAL_ESCOLAR_VISIBLE && (
                <TouchableOpacity
                  style={[styles.card, selectedVehicle === 'SCHOOL_BUS' && styles.cardSelected]}
                  onPress={() => setSelectedVehicle('SCHOOL_BUS')}
                  activeOpacity={0.8}
                >
                  <View style={[styles.iconCircle, selectedVehicle === 'SCHOOL_BUS' && styles.iconCircleSelected]}>
                    <Image source={require('../../assets/images/buss escolar.png')} style={styles.vehicleIcon} resizeMode="contain" />
                  </View>
                  <Text style={[styles.cardTitle, selectedVehicle === 'SCHOOL_BUS' && styles.textSelected]}>Transporte Escolar</Text>
                  <Text style={styles.cardDesc}>SUV de 3 filas (jeepeta) o Minivan (Premium/Alta Seguridad)</Text>
                  <Text style={styles.feeBadge}>Cuota: RD$ 2,500/mes (o eq. local)</Text>
                </TouchableOpacity>
              )}
            </View>
          </>
        )}
      </ScrollView>

      <View
        style={[styles.footer, { paddingBottom: SIZES.padding + margenAbajo }]}
        onLayout={(e) => {
          const alto = Math.round(e.nativeEvent.layout.height);
          // Solo si cambió de verdad: `onLayout` se dispara también por cosas
          // que no cambian el alto, y un setState por cada uno sería un bucle
          // de renders.
          if (alto > 0 && alto !== altoDelPie) setAltoDelPie(alto);
        }}
      >
        <Button
          title="Continuar"
          onPress={handleNext}
          disabled={esChofer && !selectedVehicle}
        />
        {/* SALIDA SIEMPRE VISIBLE, CON TEXTO. El footer está posicionado en
            absoluto, así que este botón no se va con el scroll ni queda tapado
            por la lista de vehículos. Solo desaparece si algún día se enciende
            el muro (KYC_WALL_ENFORCED = true), que es el único caso en que la
            verificación SÍ es obligatoria para entrar. */}
        {!KYC_WALL_ENFORCED && (
          <>
            <TouchableOpacity
              onPress={handleAhoraNo}
              style={styles.ahoraNoBtn}
              activeOpacity={0.7}
              accessibilityRole="button"
            >
              <Text style={styles.ahoraNoText}>Ahora no, entrar a la app</Text>
            </TouchableOpacity>
            <Text style={styles.ahoraNoNota}>
              {esChofer
                ? 'Puedes entrar y conocer la app ahora mismo. Para ponerte en línea y aceptar viajes sí hay que terminar esta verificación.'
                : role === 'PASSENGER'
                ? 'Puedes entrar al mapa y pedir tu viaje sin verificarte. Cuando quieras tu escudo verde, vuelves desde tu perfil.'
                : 'Puedes entrar y conocer la app ahora mismo. Cuando quieras tu escudo verde, vuelves desde tu perfil.'}
            </Text>
          </>
        )}
      </View>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    padding: SIZES.padding,
    // Valor de arranque para el primer fotograma. El de verdad lo pone
    // `altoDelPie` (medido con onLayout) en el contentContainerStyle de la
    // lista — este 230 solo vive hasta que llega esa medida.
    paddingBottom: 230,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  header: {
    marginTop: Platform.OS === 'ios' ? 10 : 20,
    marginBottom: 30,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    color: Colors.primary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: SIZES.font,
    color: Colors.textSecondary,
    lineHeight: 22,
  },
  sectionTitle: {
    fontSize: SIZES.large,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 16,
  },
  optionsContainer: {
    gap: 16,
  },
  card: {
    backgroundColor: Colors.white,
    borderRadius: SIZES.radius,
    padding: 20,
    borderWidth: 2,
    borderColor: Colors.border,
  },
  cardSelected: {
    borderColor: Colors.accent,
    backgroundColor: '#F0FFF5',
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  iconCircleSelected: {
    backgroundColor: '#E8F5E9',
  },
  vehicleIcon: {
    width: 48,
    height: 48,
  },
  cardTitle: {
    fontSize: SIZES.large,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 4,
  },
  textSelected: {
    color: Colors.accent,
  },
  cardDesc: {
    fontSize: SIZES.font,
    color: Colors.textSecondary,
    marginBottom: 12,
  },
  feeBadge: {
    backgroundColor: Colors.primary,
    color: Colors.white,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
    fontSize: 12,
    fontWeight: 'bold',
    overflow: 'hidden',
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: SIZES.padding,
    backgroundColor: Colors.background,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  backButton: {
    padding: 8,
    alignSelf: 'flex-start',
    marginBottom: 10,
    marginTop: Platform.OS === 'ios' ? 10 : 20,
  },
  stickyHeader: {
    paddingHorizontal: SIZES.padding,
    paddingTop: Platform.OS === 'ios' ? 8 : 16,
    paddingBottom: 8,
    backgroundColor: Colors.background,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  stickyBackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
  },
  stickyBackText: {
    color: Colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  stickySignOutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 59, 48, 0.08)',
  },
  stickySignOutText: {
    color: '#FF3B30',
    fontSize: 14,
    fontWeight: '600',
  },
  // "Ahora no": botón secundario, pero con borde y texto de contraste real —
  // un enlace gris pálido sobre fondo claro se lee como texto muerto y el
  // usuario no lo toca (es la misma trampa de "el botón existe pero es
  // invisible" que ya nos costó un rechazo).
  ahoraNoBtn: {
    marginTop: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ahoraNoText: {
    color: Colors.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  ahoraNoNota: {
    marginTop: 8,
    fontSize: 12,
    lineHeight: 16,
    color: Colors.textSecondary,
    textAlign: 'center',
  },
});
